import { apiSearch } from "@/common/services";
import { THistoryList } from "@/common/api/services/types";
import { useUserStore } from "@/common/store/userStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";

const MAX_DESKTOP_ITEMS = 5;
const MAX_MOBILE_ITEMS = 3;

interface IUseRecentSearches {
  handleSearchHistory: (hasHistory: boolean) => void;
}

export const useRecentSearches = ({ handleSearchHistory }: IUseRecentSearches) => {
  const [historySearches, setHistorySearches] = useState<THistoryList>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const currentUser = useUserStore((state) => state.currentUser);
  const deviceType = useDeviceType();
  const isPhone = useMemo(() => deviceType === deviceTypeEnum.PHONE, [deviceType]);

  const isUserLoggedIn = useMemo(() => {
    return currentUser && (Object.entries(currentUser).length > 0);
  }, [currentUser]);

  const getHistorySearches = useCallback(async () => {
    try {
      setLoading(true);
      const historyItems = await apiSearch.getHistorySearches();
      const filteredHistoryItems = historyItems.slice(0, isPhone ? MAX_MOBILE_ITEMS : MAX_DESKTOP_ITEMS);
      setHistorySearches(filteredHistoryItems);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isPhone]);

  const onClearAllHistory = useCallback(async () => {
    try {
      setHistorySearches([]);
      await apiSearch.removeAllHistorySearches();
    } catch (error) {
      console.error(error);
    }
  }, []);

  const onClearHistoryItem = useCallback(async (id: string) => {
    try {
      const updatedHistoryItems = historySearches.filter((item) => item.id !== id);
      setHistorySearches(updatedHistoryItems);
      await apiSearch.removeHistorySearchItem(id);
    } catch (error) {
      console.error(error);
    }
  }, [historySearches]);

  useEffect(() => {
    if (isUserLoggedIn) {
      getHistorySearches();
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    handleSearchHistory(historySearches.length > 0);
  }, [historySearches]);

  return {
    historySearches,
    onClearAllHistory,
    onClearHistoryItem,
    loading
  };
};
